import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { formatMoney, toMinorUnits } from "@tovi/shared";
import { Avatar, Badge, Button, Modal, useTranslate } from "@tovi/ui";
import { api } from "@/lib/api.ts";
import { shiftErrorMessage } from "@/lib/shift-error.ts";
import { currentCashier, signOutCashier, staffHeaders } from "@/lib/staff.ts";
import { typedMinorUnits } from "@/lib/typed-amount.ts";
import { useEmployeePhoto } from "@/lib/use-employee-photo.ts";

/**
 * The drawer (`10-build-plan.md` § Phase 2, "shift handover").
 *
 * The API has had shifts since phase 2 and the till never called them, so a
 * cash-up meant a manager opening the back office on a laptop. This is the
 * till's side: open with a float, watch the takings build, and count the
 * drawer at the end.
 *
 * The number that matters is the last one — counted against expected. It is
 * shown as a difference and not as two figures to subtract, because a cashier
 * at the end of a service is the last person who should be doing arithmetic.
 *
 * Card takings are shown but excluded from the expectation, which is the
 * distinction people get wrong: money that never went into the drawer cannot
 * come out of it.
 */

type Takings = {
  orderCount: number;
  billed: string;
  refunded: string;
  net: string;
  byMethod: {
    methodCode: string;
    methodName: string;
    taken: string;
    refunded: string;
    net: string;
  }[];
};

type Shift = {
  id: string;
  number: string;
  status: "open" | "closed";
  openedByName: string | null;
  openingFloat: string;
  currency: string;
  takings: Takings;
};

export function ShiftPanel({ onClose }: { onClose: () => void }) {
  const t = useTranslate();
  const queryClient = useQueryClient();
  const cashier = currentCashier();
  const cashierPhoto = useEmployeePhoto(cashier?.id ?? null, cashier?.photoVersion ?? null);
  const [float, setFloat] = useState("");
  const [counted, setCounted] = useState("");
  const [busy, setBusy] = useState(false);
  /** What each rail says it took, keyed by method code. Blank means unchecked. */
  const [railCounts, setRailCounts] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  const current = useQuery({
    queryKey: ["shift", "current"],
    // A 404 is "no shift open", which is a state and not a failure. Anything
    // else is left to throw.
    queryFn: () =>
      api<Shift>("/store/shifts/current").catch((caught: unknown) => {
        if (caught instanceof Error && "code" in caught && caught.code === "NOT_FOUND") return null;
        throw caught;
      }),
    refetchInterval: 15_000,
  });

  const shift = current.data ?? null;
  const currency = shift?.currency ?? "USD";
  const cash = shift?.takings.byMethod.find((row) => row.methodCode === "CASH");

  /**
   * What the drawer should hold: the float plus cash taken, less cash given
   * back. The server computes this again at close — this is the same sum shown
   * early so a cashier is not counting blind.
   */
  const expected = shift ? BigInt(shift.openingFloat) + BigInt(cash?.net ?? "0") : 0n;
  const countedMinor = typedMinorUnits(counted, currency);
  const difference = countedMinor === null ? null : countedMinor - expected;

  /**
   * The rails a cashier can be asked about: everything but cash, which the
   * drawer above already answers. Only what was filled in is sent — a rail
   * nobody checked has no answer, which is a different fact from zero. A rail
   * that was filled in but does not parse yet holds Close shut rather than
   * being dropped, the same as a bad Counted does — a typo silently omitted
   * would read to the server as agreement.
   */
  const rails = (shift?.takings.byMethod ?? []).filter((row) => row.methodCode !== "CASH");
  const railMinors = new Map<string, bigint | null>(
    rails
      .filter((row) => (railCounts[row.methodCode] ?? "").trim() !== "")
      .map(
        (row) => [row.methodCode, typedMinorUnits(railCounts[row.methodCode]!, currency)] as const,
      ),
  );
  const railsUnparseable = [...railMinors.values()].some((minor) => minor === null);
  const declaredRails = [...railMinors.entries()]
    .filter((entry): entry is [string, bigint] => entry[1] !== null)
    .map(([methodCode, minor]) => ({ methodCode, countedAmount: minor.toString() }));

  async function act(run: () => Promise<unknown>): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await run();
      await queryClient.invalidateQueries({ queryKey: ["shift"] });
      setFloat("");
      setCounted("");
      setRailCounts({});
    } catch (caught) {
      setError(shiftErrorMessage(caught, t));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open title={t("pos.shift.title")} onClose={onClose}>
      <div className="flex flex-col gap-4">
        {cashier ? (
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Avatar name={cashier.name} src={cashierPhoto ?? undefined} size={32} />
              <div>
                <p className="text-sm font-medium text-ink-body">{cashier.name}</p>
                {cashier.roleName ? (
                  <p className="text-xs text-ink-muted">{cashier.roleName}</p>
                ) : null}
              </div>
            </div>
            {/* Handing over is a change of name on the drawer, not a logout:
                the terminal stays signed in and the bills stay open. */}
            <Button variant="secondary" onClick={signOutCashier}>
              {t("pos.shift.handOver")}
            </Button>
          </div>
        ) : null}

        {shift === null ? (
          <div className="flex flex-col gap-3">
            <p className="text-sm text-ink-muted">{t("pos.shift.noneOpen")}</p>
            <label className="text-sm text-ink-body" htmlFor="shift-float">
              {t("pos.shift.openingFloat")}
            </label>
            <input
              id="shift-float"
              inputMode="decimal"
              value={float}
              onChange={(event) => setFloat(event.target.value)}
              className="rounded-control border border-line bg-surface px-3 py-3 text-lg text-ink-body"
            />
            <Button
              loading={busy}
              onClick={() =>
                void act(() =>
                  api("/store/shifts", {
                    method: "POST",
                    headers: staffHeaders(),
                    body: {
                      openingFloat:
                        float.trim() === "" ? "0" : toMinorUnits(float.trim(), currency),
                    },
                  }),
                )
              }
            >
              {t("pos.shift.open")}
            </Button>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-ink-muted">
                {t("pos.shift.number", { number: shift.number })}
              </span>
              <Badge tone="success">{t("pos.shift.openNow")}</Badge>
            </div>

            <dl className="flex flex-col gap-1 text-sm">
              <Row label={t("pos.shift.openedBy")} value={shift.openedByName ?? "—"} />
              <Row
                label={t("pos.shift.openingFloat")}
                value={formatMoney(shift.openingFloat, currency)}
              />
              <Row label={t("pos.shift.bills")} value={String(shift.takings.orderCount)} />
            </dl>

            <section className="flex flex-col gap-1">
              <h3 className="text-label font-semibold text-ink-body">{t("pos.shift.byMethod")}</h3>
              {shift.takings.byMethod.length === 0 ? (
                <p className="text-sm text-ink-subtle">{t("pos.shift.nothingYet")}</p>
              ) : (
                shift.takings.byMethod.map((row) => (
                  <Row
                    key={row.methodCode}
                    label={row.methodName}
                    value={formatMoney(row.net, currency)}
                  />
                ))
              )}
            </section>

            <section className="flex flex-col gap-2 rounded-card border border-line p-3">
              <Row
                label={t("pos.shift.expectedInDrawer")}
                value={formatMoney(expected.toString(), currency)}
              />
              {/* Said out loud, because it is the thing people get wrong. */}
              <p className="text-xs text-ink-subtle">{t("pos.shift.cardNotInDrawer")}</p>

              <label className="mt-1 text-sm text-ink-body" htmlFor="shift-counted">
                {t("pos.shift.counted")}
              </label>
              <input
                id="shift-counted"
                inputMode="decimal"
                value={counted}
                onChange={(event) => setCounted(event.target.value)}
                aria-invalid={counted.trim() !== "" && countedMinor === null}
                className="rounded-control border border-line bg-surface px-3 py-3 text-lg text-ink-body"
              />

              {/* A difference, not two numbers to subtract: nobody at the end
                  of a service should be doing arithmetic. */}
              {difference !== null ? (
                <p
                  className={`text-sm font-medium ${
                    difference === 0n
                      ? "text-success"
                      : difference < 0n
                        ? "text-danger"
                        : "text-warning-ink"
                  }`}
                >
                  {difference === 0n
                    ? t("pos.shift.balances")
                    : difference < 0n
                      ? t("pos.shift.short", {
                          amount: formatMoney((-difference).toString(), currency),
                        })
                      : t("pos.shift.over", {
                          amount: formatMoney(difference.toString(), currency),
                        })}
                </p>
              ) : null}

              {/* Most takings here are not cash. A drawer that balances says
                  nothing about the seven bills that arrived by bank transfer,
                  so the rails get asked about too — optionally, because a
                  cashier who checked two apps of five should record two. */}
              {rails.length > 0 ? (
                <section className="mt-2 flex flex-col gap-2 border-t border-line pt-3">
                  <h3 className="text-label font-semibold text-ink-body">
                    {t("pos.shift.railCounts")}
                  </h3>
                  <p className="text-xs text-ink-subtle">{t("pos.shift.railCountsHint")}</p>
                  {rails.map((row) => (
                    <label key={row.methodCode} className="flex items-center gap-2 text-sm">
                      <span className="flex-1 text-ink-body">
                        {row.methodName}
                        <span className="block text-xs text-ink-subtle">
                          {formatMoney(row.net, currency)}
                        </span>
                      </span>
                      <input
                        inputMode="decimal"
                        aria-label={row.methodName}
                        value={railCounts[row.methodCode] ?? ""}
                        onChange={(event) =>
                          setRailCounts((prev) => ({
                            ...prev,
                            [row.methodCode]: event.target.value,
                          }))
                        }
                        aria-invalid={railMinors.get(row.methodCode) === null}
                        className="w-32 rounded-control border border-line bg-surface px-3 py-2 text-ink-body"
                      />
                    </label>
                  ))}
                </section>
              ) : null}

              <Button
                loading={busy}
                disabled={countedMinor === null || railsUnparseable}
                onClick={() =>
                  void act(() =>
                    api(`/store/shifts/${shift.id}/close`, {
                      method: "POST",
                      headers: staffHeaders(),
                      body: {
                        countedCash: countedMinor!.toString(),
                        tenderCounts: declaredRails,
                      },
                    }),
                  )
                }
              >
                {t("pos.shift.close")}
              </Button>
            </section>
          </div>
        )}

        {error ? <p className="text-sm text-danger">{error}</p> : null}
      </div>
    </Modal>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-ink-muted">{label}</dt>
      <dd className="text-ink-body">{value}</dd>
    </div>
  );
}
